import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: 1000, suffix: '+', label: 'Avaliações Positivas', icon: 'ri-star-smile-line' },
  { value: 5, suffix: '+', label: 'Anos de Experiência', icon: 'ri-award-line' },
  { value: 4, suffix: '', label: 'Cidades Atendidas', icon: 'ri-map-pin-2-line' },
  { value: 100, suffix: '+', label: 'Revendedores Ativos', icon: 'ri-store-3-line' },
];

const ResellerCTA = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [counts, setCounts] = useState(stats.map(() => 0));
  const animatedRef = useRef(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.querySelectorAll('.reveal').forEach((el) => {
              el.classList.add('revealed');
            });
            if (!animatedRef.current) {
              animatedRef.current = true;
              stats.forEach((stat, i) => {
                const duration = 2000;
                const start = performance.now();
                const animate = (now: number) => {
                  const progress = Math.min((now - start) / duration, 1);
                  const eased = 1 - Math.pow(1 - progress, 3);
                  setCounts((prev) => {
                    const next = [...prev];
                    next[i] = Math.floor(eased * stat.value);
                    return next;
                  });
                  if (progress < 1) requestAnimationFrame(animate);
                };
                requestAnimationFrame(animate);
              });
            }
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="revenda"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-gradient-to-br from-secondary-100/60 via-background-100 to-accent-50/40 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="absolute top-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-accent-100/30 animate-blob" />
      <div className="absolute bottom-[-80px] left-[-80px] w-[400px] h-[400px] rounded-full bg-primary-100/25 animate-blob animation-delay-2000" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 md:mb-18">
            <span className="reveal inline-flex items-center gap-2 font-label text-xs md:text-sm text-accent-600 tracking-widest uppercase mb-4">
              <span className="w-6 h-px bg-accent-400" />
              Seja um Parceiro
              <span className="w-6 h-px bg-accent-400" />
            </span>
            <h2 className="reveal reveal-delay-1 font-display text-3xl md:text-5xl lg:text-[56px] text-foreground-900 leading-tight mb-4">
              Revenda <span className="text-accent-600">Naturalle</span>
            </h2>
            <p className="reveal reveal-delay-2 font-body text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              Distribuímos nossos produtos para supermercados, conveniências, lanchonetes, cantinas, academias e diversos estabelecimentos comerciais.
            </p>
          </div>

          {/* Stats cards */}
          <div className="reveal reveal-delay-3 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-12 md:mb-16 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className="glass-warm rounded-3xl p-5 md:p-6 text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-accent-600`} />
                </div>
                <div className="font-display text-2xl md:text-3xl text-accent-600">
                  {counts[index].toLocaleString('pt-BR')}{stat.suffix}
                </div>
                <div className="font-label text-xs text-foreground-500 mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="reveal reveal-delay-4 flex flex-wrap justify-center gap-3 md:gap-4">
            <a
              href="https://wa.me/5585982245030?text=Olá!%20Vim%20pelo%20site%20da%20Naturalle%20Sanduíches%20e%20tenho%20interesse%20em%20ser%20revendedor.%20Gostaria%20de%20mais%20informações."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-label text-sm px-8 py-4 rounded-full bg-accent-500 text-white hover:bg-accent-600 transition-all duration-300 shadow-sm shadow-accent-200/50 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line" />
              <span>Quero ser revendedor</span>
            </a>
            <a
              href="https://wa.me/5585982245030?text=Olá!%20Vim%20pelo%20site%20da%20Naturalle%20Sanduíches%20e%20gostaria%20de%20solicitar%20atendimento%20comercial."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-label text-sm px-8 py-4 rounded-full border border-foreground-200 text-foreground-700 hover:border-accent-400 hover:text-accent-600 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-customer-service-2-line" />
              <span>Solicitar atendimento</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResellerCTA;